import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";

export const EditExercisesDialog = ({
  open,
  onClose,
  token,
  selectedExerciseIds,
  onSave,
}) => {
  const [allExercises, setAllExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const { darkMode } = useContext(ThemeContext);

  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/exercise`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAllExercises(response.data);
        setSelectedExercises(
          response.data.filter((exercise) =>
            selectedExerciseIds.includes(exercise.id)
          )
        );
      } catch (err) {
        console.error("Error fetching exercises:", err);
      }
    };

    if (open) {
      fetchExercises();
    }
  }, [open, token, selectedExerciseIds]);

  const handleAddToSelected = (exercise) => {
    setSelectedExercises((prev) => [...prev, exercise]);
  };

  const handleRemoveFromSelected = (exerciseId) => {
    setSelectedExercises((prev) =>
      prev.filter((exercise) => exercise.id !== exerciseId)
    );
  };

  const availableExercises = allExercises.filter(
    (exercise) =>
      !selectedExercises.some((selected) => selected.id === exercise.id)
  );

  const handleSave = () => {
    onSave(selectedExercises.map((exercise) => exercise.id));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Exercises</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Selected Exercises
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            {selectedExercises.map((exercise) => {
              const img = require(`../../assets/exercise/${exercise.bmname}.png`);
              return (
                <Box
                  key={exercise.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: darkMode
                      ? getColors.thumbnailBorderDark
                      : getColors.thumbnailBorderLight,
                    "&:hover": {
                      borderColor: darkMode
                        ? getColors.thumbnailTextDark
                        : getColors.thumbnailTextLight,
                    },
                    borderRadius: "5px",
                    padding: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRemoveFromSelected(exercise.id)}
                >
                  <img
                    src={img}
                    alt={exercise.name}
                    style={{
                      width: "50px",
                      height: "50px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    {exercise.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Exercises
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            {availableExercises.map((exercise) => {
              const img = require(`../../assets/exercise/${exercise.bmname}.png`);
              return (
                <Box
                  key={exercise.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: darkMode
                      ? getColors.thumbnailBorderDark
                      : getColors.thumbnailBorderLight,
                    "&:hover": {
                      borderColor: darkMode
                        ? getColors.thumbnailTextDark
                        : getColors.thumbnailTextLight,
                    },
                    borderRadius: "5px",
                    padding: "8px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleAddToSelected(exercise)}
                >
                  <img
                    src={img}
                    alt={exercise.name}
                    style={{
                      width: "50px",
                      height: "50px",
                      marginRight: "8px",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: darkMode
                        ? getColors.drawerTextDark
                        : getColors.thumbnailTextLight,
                    }}
                  >
                    {exercise.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: darkMode ? getColors.appBarBgLight : getColors.appBarBgDark,
            "&: hover": {
              background: "none",
              color: getColors.primaryMain,
            },
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
